import {useCallback, useEffect, useState} from "react";
import { useParams, Link } from "react-router-dom";
import {getEmployeeData, getPayoutsByEmployee, getTipsByEmployee} from "../../services/firestoreService";

/**
 * Admin view for managing payouts of an employee.
 */
function AdminEmployeeDashboard() {
    const { businessID, employeeID } = useParams();
    const [employee, setEmployee] = useState(null);
    const [tips, setTips] = useState([]);
    const [payouts, setPayouts] = useState([]);

    const fetchDataV2 = useCallback(async () => {
        const [employeeData, tipsData, payoutsData] = await Promise.all([
            getEmployeeData(businessID, employeeID),
            getTipsByEmployee(businessID, employeeID),
            getPayoutsByEmployee(businessID, employeeID),
        ]);

        setEmployee(employeeData);
        setTips(tipsData);
        setPayouts(payoutsData);
    }, [businessID, employeeID]);

    useEffect(() => {
        fetchDataV2();
    }, [fetchDataV2]);

    // Filter pending tips (those not yet in a payout)
    const pendingTips = tips.filter(tip => tip.payoutStatus === "PENDING");

    // Calculate total amount for payout
    const totalPendingAmount = pendingTips.reduce((sum, tip) => sum + tip.amount, 0);

    // Calculate Statistics
    const totalTips = tips.reduce((sum, tip) => sum + tip.amount, 0);
    const avgTipSize = tips.length > 0 ? (totalTips / tips.length).toFixed(2) : 0;
    const avgRating = tips.length > 0
        ? (tips.reduce((sum, tip) => sum + tip.rating, 0) / tips.length).toFixed(1)
        : "N/A";
    const totalPayouts = payouts.reduce((sum, payout) => sum + payout.totalAmount, 0);

    // Calculate tips per day
    const tipDays = new Set(tips.map(tip => new Date(tip.createdAt.seconds * 1000).toDateString()));
    const avgTipsPerDay = tipDays.size > 0 ? (totalTips / tipDays.size).toFixed(2) : "N/A";
    const sortedTips = [...tips].sort((a, b) => {
        const aTs = a?.createdAt?.seconds || 0;
        const bTs = b?.createdAt?.seconds || 0;
        return bTs - aTs;
    });
    const sortedPayouts = [...payouts].sort((a, b) => {
        const aTs = a?.createdAt?.seconds || 0;
        const bTs = b?.createdAt?.seconds || 0;
        return bTs - aTs;
    });

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Admin Employee Dashboard</h1>
                <Link to={`/admin/business/${businessID}`} className="text-blue-600">⬅ Back to Business</Link>
            </div>
            <p className="text-gray-600">Business ID: {businessID}</p>
            <p className="text-gray-600">Employee ID: {employeeID}</p>
            <p className="text-gray-600">First Name: {employee?.firstName || "N/A"}</p>
            <p className="text-gray-600">Last Name: {employee?.lastName || "N/A"}</p>
            <p className="text-gray-600">IBAN: {employee?.iban || "N/A"}</p>

            {/* Statistics Section */}
            <section className="mt-6">
                <h2 className="text-xl font-semibold">Statistics</h2>
                <div className="grid grid-cols-2 gap-4 p-4 border rounded bg-gray-50 shadow-md">
                    <p className="text-sm font-medium">📊 Avg Rating: <span className="font-bold">{avgRating}/5</span></p>
                    <p className="text-sm font-medium">💰 Avg Tip: <span className="font-bold">€{avgTipSize}</span></p>
                    <p className="text-sm font-medium">📅 Avg Tips Per Day: <span className="font-bold">€{avgTipsPerDay}</span></p>
                    <p className="text-sm font-medium">💸 Total Payouts: <span className="font-bold">€{totalPayouts}</span></p>
                </div>
            </section>

            {/* Recent Tips Section */}
            <section className="mt-6">
                <h2 className="text-xl font-semibold">Recent Tips</h2>
                <ul className="border rounded p-4 bg-white shadow-md">
                    {sortedTips.slice(0, 5).map(tip => (
                        <li key={tip.id} className="py-2 border-b flex justify-between items-center">
                            <div>
                                <p className="font-medium">💰 €{tip.amount}</p>
                                <p className="text-sm text-gray-600">{tip.feedback || "No feedback"}</p>
                                <p className="text-sm text-gray-500">⭐ {tip.rating}/5</p>
                                <p className="text-sm text-gray-500">Payout Status: {tip.payoutStatus || "N/A"}</p>
                                <p className="text-sm text-gray-500">🕒 {new Date(tip.createdAt.seconds * 1000).toLocaleString()}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Payout History Section */}
            <section className="mt-6">
                <h2 className="text-xl font-semibold">Payout History</h2>
                <ul className="border rounded p-4 bg-gray-50 shadow-md">
                    {sortedPayouts.slice(0, 5).map(payout => (
                        <li key={payout.id} className="py-2 border-b">
                            <strong>💸 €{payout.totalAmount}</strong> - {payout.status}
                            <p className="text-sm text-gray-500">Payout ID: {payout.id}</p>
                            <p className="text-sm text-gray-500">🕒 {new Date(payout.createdAt.seconds * 1000).toLocaleString()}</p>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Pending Payouts Section */}
            <section className="mt-6">
                <div className="flex items-center justify-between gap-4">
                    <h2 className="text-xl font-semibold">Pending Payouts</h2>
                    <Link to="/admin/payouts" className="text-blue-600 underline">Open payout export</Link>
                </div>
                <ul className="border rounded p-4 bg-yellow-50 shadow-md">
                    {pendingTips.length > 0 ? (
                        <>
                            {pendingTips.map(tip => (
                                <li key={tip.id} className="py-2 border-b flex justify-between items-center">
                                    <div>
                                        <p className="text-sm font-semibold">Tip ID: {tip.id}</p>
                                        <p className="text-sm font-medium text-red-600">💰 €{tip.amount}</p>
                                    </div>
                                </li>
                            ))}
                            <li className="py-2 flex justify-between items-center font-bold">
                                <p>Total Pending:</p>
                                <p>💰 €{totalPendingAmount.toFixed(2)}</p>
                            </li>
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">No pending payouts.</p>
                    )}
                </ul>
            </section>

        </div>
    );
}

export default AdminEmployeeDashboard;
